import React, { useState } from 'react';
import { Check } from 'lucide-react';

const IrisStandard = ({ formConfig = {}, onNext }) => {
  const {
    title = "IRIS Metrics",
    submitLabel = "Submit",
    metrics = []
  } = formConfig;
  const [selectedMetrics, setSelectedMetrics] = useState({});
  const [formValues, setFormValues] = useState({});

  const handleCheckboxChange = (id) => {
    const newSelected = !selectedMetrics[id];
    setSelectedMetrics(prev => ({
      ...prev,
      [id]: newSelected
    }));
  };

  const handleInputChange = (id, value) => {
    setFormValues(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleDropdownChange = (id, field, value) => {
    setFormValues(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value
      }
    }));
  };

  const parseDefinition = (definition) => {
    // Check if definition contains dropdown options
    const selectMatch = definition.match(/Select (one|any that apply):\s*\n([\s\S]*?)(?=\n\n|$)/);
    if (selectMatch) {
      const optionsText = selectMatch[2];
      const options = optionsText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.startsWith('-'))
        .map(line => line.replace(/^-\s*/, '').trim());

      return {
        hasDropdown: true,
        selectType: selectMatch[1],
        options,
        text: definition.split(selectMatch[0])[0].trim()
      };
    }
    return { hasDropdown: false, text: definition };
  };

  const renderReportingFormatInput = (metric) => {
    const id = metric.ID;
    const format = metric['Reporting Format'];
    const isDisabled = false;

    const inputClass = `input-field ${isDisabled ? 'input-disabled' : ''}`;

    if (format === 'Yes/No') {
      return (
        <select
          className={inputClass}
          value={formValues[id] || ''}
          onChange={(e) => handleInputChange(id, e.target.value)}
          disabled={isDisabled}
        >
          <option value="">Select</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      );
    } else if (format === 'Text') {
      return (
        <textarea
          className={`${inputClass} textarea-field`}
          rows="2"
          placeholder="Enter text..."
          value={formValues[id] || ''}
          onChange={(e) => handleInputChange(id, e.target.value)}
          disabled={isDisabled}
        />
      );
    } else if (format === 'Decimal') {
      return (
        <input
          type="number"
          step="0.01"
          className={inputClass}
          placeholder="Enter decimal value"
          value={formValues[id] || ''}
          onChange={(e) => handleInputChange(id, e.target.value)}
          disabled={isDisabled}
        />
      );
    } else if (format?.includes('Unit Referenced') || format?.includes('Unit of Measure') || format?.includes('Currency')) {
      return (
        <input
          type="number"
          className={inputClass}
          placeholder="Enter value"
          value={formValues[id] || ''}
          onChange={(e) => handleInputChange(id, e.target.value)}
          disabled={isDisabled}
        />
      );
    } else {
      return (
        <input
          type="text"
          className={inputClass}
          placeholder="Enter value"
          value={formValues[id] || ''}
          onChange={(e) => handleInputChange(id, e.target.value)}
          disabled={isDisabled}
        />
      );
    }
  };

  const handleSubmit = () => {
    const selectedData = Object.keys(selectedMetrics)
      .filter(id => selectedMetrics[id])
      .map(id => {
        const metric = metrics.find(m => m.ID === id);
        return {
          ...metric,
          value: formValues[id]
        };
      });

    if (onNext) {
      onNext(selectedData);
    }
  };

  // Show message if no metrics provided
  if (!metrics || metrics.length === 0) {
    return (
      <div className="container">
        <h2 className="title">{title}</h2>
        <div className="no-metrics">
          <p className="no-metrics-text">No metrics available. Please provide metrics data.</p>
          <p className="no-metrics-debug">Metrics array is: {JSON.stringify(metrics)}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container">
        <h2 className="title">{title}</h2>

        {/* User hint for the selection workflow */}
        <div className="hint-banner">
          <p className="hint-text">
            <strong>Tip:</strong> Check the checkboxes to select metrics for reporting.
          </p>
        </div>

        <div className="table-wrapper">
          <table className="metrics-table">
            <thead>
              <tr className="table-header">
                <th className="table-header-cell select-cell">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={Object.values(selectedMetrics).every(v => v) && Object.keys(selectedMetrics).length === metrics.length}
                    onChange={(e) => {
                      const allSelected = e.target.checked;
                      const newState = {};
                      metrics.forEach(m => {
                        newState[m.ID] = allSelected;
                      });
                      setSelectedMetrics(newState);
                    }}
                  />
                </th>
                <th className="table-header-cell">ID</th>
                <th className="table-header-cell">Metric Name</th>
                <th className="table-header-cell">Definition</th>
                <th className="table-header-cell">Reporting Format</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, index) => {
                const parsedDef = parseDefinition(metric.Definition || '');
                const isSelected = selectedMetrics[metric.ID];

                return (
                  <tr
                    key={metric.ID}
                    className={`table-row ${index % 2 === 0 ? 'row-even' : 'row-odd'}`}
                  >
                    <td className="table-cell checkbox-cell">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={isSelected || false}
                        onChange={() => handleCheckboxChange(metric.ID)}
                      />
                    </td>
                    <td className="table-cell id-cell">
                      {metric.ID}
                    </td>
                    <td className="table-cell name-cell">
                      {metric['Metric Name']}
                    </td>
                    <td className="table-cell definition-cell">
                      <div className="definition-content">
                        <p className="definition-text">{parsedDef.text}</p>
                        {parsedDef.hasDropdown && (
                          <div className="dropdown-wrapper">
                            <select
                              className={`input-field ${!isSelected ? 'input-disabled' : ''}`}
                              value={(typeof formValues[metric.ID] === 'object' ? formValues[metric.ID]?.definitionSelect : '') || ''}
                              onChange={(e) => handleDropdownChange(metric.ID, 'definitionSelect', e.target.value)}
                              disabled={false}
                            >
                              <option value="">Select {parsedDef.selectType}...</option>
                              {parsedDef.options.map((opt, i) => (
                                <option key={i} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell format-cell">
                      <div className="format-content">
                        <div className="format-label">{metric['Reporting Format']}</div>
                        {renderReportingFormatInput(metric)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="submit-section">
          <button
            onClick={handleSubmit}
            disabled={Object.keys(selectedMetrics).filter(id => selectedMetrics[id]).length === 0}
            className="submit-button"
          >
            {submitLabel}
            <Check size={20} />
          </button>
        </div>
      </div>

      <style>{`
        /* Container and Layout */
        .container {
          width: 100%;
          max-width: 80rem;
          margin: 0 auto;
          padding: 1.5rem;
          background-color: transparent; /* Removed bg-white */
          border-radius: 0.5rem;
          box-shadow: none; /* Removed shadow-lg */
        }

        /* Title */
        .title {
          font-size: 1.5rem;
          line-height: 2rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 1.5rem;
        }

        /* Hint Banner */
        .hint-banner {
          margin-bottom: 1rem;
          padding: 0.75rem;
          background-color: transparent; /* Removed bg-blue-50 */
          border: none; /* Removed border */
          border-radius: 0.375rem;
        }

        .hint-text {
          font-size: 0.875rem;
          color: #1e40af;
          margin: 0;
        }

        /* Table Wrapper */
        .table-wrapper {
          overflow-x: auto;
          overflow-y: auto;
          max-height: 500px; /* Adjust as needed for vertical scroll threshold */
        }

        /* Table */
        .metrics-table {
          width: 100%;
          border-collapse: collapse;
        }

        /* Table Header */
        .table-header {
color: #000000;
        }

        .table-header-cell {
          border: 1px solid #9a3412;
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .select-cell {
          width: 3rem;
        }

        /* Table Rows */
        .table-row {
          transition: background-color 0.15s ease-in-out;
        }

        .table-row:hover {
          background-color: #eff6ff; /* Kept hover for usability */
        }

        .row-even {
          background-color: transparent;
        }

        .row-odd {
          background-color: transparent; /* Removed bg-gray-50 */
        }

        /* Table Cells */
        .table-cell {
          border: 1px solid #d1d5db;
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          vertical-align: top;
        }

        .checkbox-cell {
          text-align: center;
          width: 3rem;
        }

        .id-cell {
          font-weight: 500;
          color: #2563eb;
        }

        .name-cell {
          font-weight: 500;
          color: #1f2937;
        }

        .definition-cell {
          color: #374151;
        }

        .format-cell .format-label {
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }

        /* Checkboxes */
        .checkbox {
          width: 1.25rem;
          height: 1.25rem;
          cursor: pointer;
          accent-color: #2563eb;
        }

        /* Definition Content */
        .definition-content {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .definition-text {
          margin: 0;
        }

        .dropdown-wrapper select {
          width: 100%;
        }

        /* Format Content */
        .format-content {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        /* Input Fields */
        .input-field {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          outline: none;
          background-color: transparent; /* Removed default bg */
        }

        .input-field:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 2px #93c5fd;
        }

        .input-field.input-disabled {
          background-color: transparent; /* Removed bg-gray-100 */
          opacity: 0.7;
        }

        .textarea-field {
          resize: none;
        }

        /* No Metrics */
        .no-metrics {
          text-align: center;
          padding: 3rem 0;
          color: #6b7280;
        }

        .no-metrics-text {
          font-size: 1.125rem;
          margin-bottom: 0.5rem;
        }

        .no-metrics-debug {
          font-size: 0.875rem;
          color: #9ca3af;
        }

        /* Submit Section */
        .submit-section {
          margin-top: 1.5rem;
          display: flex;
          justify-content: flex-end;
        }

        .submit-button {
          height: 64px;
          background: #02462E;
          color: #ffffff;
          font-family: "DM Sans", sans-serif;
          font-size: 24px;
          font-weight: 700;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          transition: background 0.15s ease-in-out;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
          padding: 0 1.5rem;
          text-transform: none;
        }

        .submit-button:hover:not(:disabled) {
          background: #023d28;
        }

        .submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .container {
            padding: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default IrisStandard;